import axios from 'axios';

const API_URL = '/api';

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const authAPI = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me'),
};

// User Management APIs
export const userAPI = {
    getAll: () => api.get('/users'),
    getOne: (id) => api.get(`/users/${id}`),
    create: (data) => api.post('/auth/register', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    updateProfile: (data) => api.put('/profile/update', data),
    changePassword: (data) => api.put('/profile/change-password', data),
};

// Label APIs
export const labelAPI = {
    getAll: () => api.get('/labels'),
    getOptions: () => api.get('/labels/options'),
    getOne: (id) => api.get(`/labels/${id}`),
    create: (data) => api.post('/labels', data),
    update: (id, data) => api.put(`/labels/${id}`, data),
    updateSelf: (data) => api.put('/labels/self', data),
    delete: (id) => api.delete(`/labels/${id}`),
    requestRevenueShareChange: (id, newPercentage) =>
        api.post(`/labels/${id}/request-revenue-share-change?new_percentage=${newPercentage}`),
    approveRevenueShare: (id, action) =>
        api.put(`/labels/${id}/approve-revenue-share?action=${action}`),

    search: (query, limit = 20) =>
        api.get('/labels/search', {
            params: { q: query, limit }
        }),

    uploadDocument: (labelId, documentType, file) => {
        const formData = new FormData();
        formData.append('document_type', documentType);
        formData.append('file', file);

        return api.post(`/labels/${labelId}/documents`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },

    downloadDocument: (labelId, documentId) =>
        api.get(`/labels/${labelId}/documents/${documentId}/download`, {
            responseType: 'blob'
        }),
    deleteDocument: (labelId, documentId) =>
    api.delete(`/labels/${labelId}/documents/${documentId}`),

};

// Song APIs
export const songAPI = {
    getAll: (labelId) => api.get('/songs', { params: { label_id: labelId } }),
    create: (data) => api.post('/songs', data),
    upload: (file, onUploadProgress) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/songs/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: onUploadProgress,
        });
    },
    getCatalogSummary: () => api.get('/songs/catalog-summary'),
    search: (query, labelId, limit = 50) => api.get('/songs/search', { 
        params: { q: query, label_id: labelId, limit } 
    }),
};

// Revenue APIs
export const revenueAPI = {
    getAll: (params) => api.get('/revenue', { params }),
    getSummary: (labelId, monthType) => api.get('/revenue/summary', { params: { label_id: labelId, month_type: monthType } }),
    getReport: (params) => api.get('/revenue/report', { params }),
    getLabelReport: (labelId, startDate, endDate) => api.get(`/revenue/label-report/${labelId}`, { 
        params: { start_date: startDate, end_date: endDate } 
    }),
    exportReport: (labelId, startDate, endDate) => {
        const params = new URLSearchParams();
        if (labelId) params.append('label_id', labelId);
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        return api.get(`/revenue/export?${params.toString()}`, { responseType: 'blob' });
    },
    upload: (file, onUploadProgress) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/revenue/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: onUploadProgress,
        });
    },
};

// Invoice APIs
export const invoiceAPI = {
    getAll: (params) => api.get('/invoices', { params }),
    create: (data) => api.post('/invoices', data),
    getAvailablePeriods: () => api.get('/invoices/available-periods'),
    updateStatus: (id, status) => api.put(`/invoices/${id}/status?status=${status}`),
    financeVet: (id, action, notes) => {
        const params = new URLSearchParams({ action });
        if (notes) params.set('notes', notes);
        return api.put(`/invoices/${id}/finance-vet?${params.toString()}`);
    },
    founderApprove: (id, action, notes) => {
        const params = new URLSearchParams({ action });
        if (notes) params.set('notes', notes);
        return api.put(`/invoices/${id}/founder-approve?${params.toString()}`);
    },
    /**
     * Mark an invoice as paid.
     * Accepts either:
     *   - markPaid(id, "old comment string") — legacy signature (still supported)
     *   - markPaid(id, { payment_comment, utr_number, paid_on }) — new object form
     */
    markPaid: (id, payload) => {
        if (typeof payload === 'string' || payload == null) {
            return api.post(
                `/invoices/${id}/mark-paid?payment_comment=${encodeURIComponent(payload || '')}`
            );
        }
        return api.post(`/invoices/${id}/mark-paid`, {
            payment_comment: payload.payment_comment || null,
            utr_number:      payload.utr_number || null,
            paid_on:         payload.paid_on || null,
        });
    },
    uploadBankReceipt: (id, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/invoices/${id}/bank-receipt`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    downloadBankReceipt: (id) => api.get(`/invoices/${id}/bank-receipt/download`),
    uploadAttachment: (id, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/invoices/${id}/attachment`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    downloadAttachment: (id) => api.get(`/invoices/${id}/download`),
    delete: (id) => api.delete(`/invoices/${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
    getAdmin: (monthType = 'reporting_month') => api.get('/dashboard/admin', { params: { month_type: monthType } }),
    getLabel: (monthType = 'reporting_month') => api.get('/dashboard/label', { params: { month_type: monthType } }),
};

export default api;
